import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePropertyDetail } from '../hooks/usePropertyDetail';
import { useDeleteProperty } from '../hooks/useDeleteProperty';
import { useUpdateProperty } from '../hooks/useUpdateProperty';
import Modal from 'react-modal';
import { ArrowLeft, Edit, Trash2, MapPin, Menu, X, PoundSterlingIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import BookingCalendar from '../components/BookingCalendar';
import ManualBlockedDatesList from '../components/ManualBlockedDatesList';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { getStatusColor } from '../utils/statusUtils';

const ViewProperties = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data, isLoading } = usePropertyDetail(id);
  const { mutate: deleteProperty, isPending: isDeleting } = useDeleteProperty();
  const { mutate: updateProperty, isPending: isUpdating } = useUpdateProperty();
  const property = data?.property;
  const bookedDates = data?.bookedDates || [];
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Prevent body scroll when status modal is open
  useEffect(() => {
    if (isStatusModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isStatusModalOpen]);

  if (isLoading) {
    return <LoadingSpinner message="Loading property details..." />;
  }

  if (!property?.id) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-lg text-prose-black/60 mb-4">Property not found</p>
        <button
          onClick={() => navigate('/properties')}
          className="px-4 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90"
        >
          Back to Properties
        </button>
      </div>
    );
  }

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    deleteProperty(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['properties'] });
        toast.success('Property deleted successfully');
        navigate('/properties');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to delete property');
      },
      onSettled: () => {
        setIsDeleteModalOpen(false);
      },
    });
  };

  const handleStatusUpdate = () => {
    if (!selectedStatus) {
      toast.error('Please select a status');
      return;
    }

    const formData = {
      title: property.title,
      description: property.description,
      address: property.address,
      type: property.type,
      pricePerNight: property.price,
      size: property.size,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      status: selectedStatus,
      newImages: [],
      features: property.features?.map(f => ({ id: f.id, name: f.feature || f })) || [],
    };

    const loadingToastId = toast.loading('Updating status...');
    updateProperty({ id, formData, originalData: property }, {
      onSuccess: () => {
        toast.dismiss(loadingToastId);
        toast.success('Status updated successfully!');
        setIsStatusModalOpen(false);
        setSelectedStatus('');
        // Manual page refresh
        window.location.reload();
      },
      onError: (error) => {
        toast.dismiss(loadingToastId);
        toast.error(error.message || 'Failed to update status');
      },
    });
  };

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-start space-x-2 sm:space-x-4 min-w-0">
          <button
            onClick={() => navigate('/properties')}
            className="p-2 hover:bg-gray-100 rounded-lg transition duration-200 flex-shrink-0"
          >
            <ArrowLeft size={24} className="text-prose-brand" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-prose-brand truncate">{property.title}</h1>
            <p className="text-prose-black/60 mt-1 flex items-center space-x-2 text-xs sm:text-sm truncate">
              <MapPin size={16} className="flex-shrink-0" />
              <span className="truncate">{property.address}</span>
            </p>
          </div>
        </div>

        <div className="relative">
          {/* Menu button for small screens */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition duration-200"
          >
            {isMenuOpen ? (
              <X size={24} className="text-prose-brand" />
            ) : (
              <Menu size={24} className="text-prose-brand" />
            )}
          </button>

          {/* Dropdown menu for small screens */}
          {isMenuOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-300 rounded-lg shadow-lg z-50 md:hidden">
              <button
                onClick={() => {
                  navigate(`/properties/${id}/edit`);
                  setIsMenuOpen(false);
                }}
                className="w-full flex items-center space-x-2 px-4 py-3 text-prose-brand hover:bg-gray-100 transition duration-200 text-sm border-b border-gray-200"
              >
                <Edit size={16} />
                <span>Edit</span>
              </button>
              <button
                onClick={() => {
                  setSelectedStatus(property.status);
                  setIsStatusModalOpen(true);
                  setIsMenuOpen(false);
                }}
                className="w-full flex items-center space-x-2 px-4 py-3 text-prose-brand hover:bg-gray-100 transition duration-200 text-sm border-b border-gray-200"
              >
                <span>Status</span>
              </button>
              <button
                onClick={() => {
                  handleDelete();
                  setIsMenuOpen(false);
                }}
                disabled={isDeleting}
                className="w-full flex items-center space-x-2 px-4 py-3 text-red-600 hover:bg-red-50 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={16} />
                <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
              </button>
            </div>
          )}

          {/* Inline buttons for large screens */}
          <div className="hidden md:flex flex-row items-center gap-2">
            <button
              onClick={() => navigate(`/properties/${id}/edit`)}
              className="flex items-center justify-center space-x-2 px-4 py-2 border border-prose-brand text-prose-brand rounded-lg hover:bg-gray-100 transition duration-200 text-sm"
            >
              <Edit size={16} />
              <span>Edit</span>
            </button>
            <button
              onClick={() => {
                setSelectedStatus(property.status);
                setIsStatusModalOpen(true);
              }}
              className="flex items-center justify-center space-x-2 px-4 py-2 border border-prose-gold text-prose-brand rounded-lg hover:bg-gray-100 transition duration-200 text-sm"
            >
              <span>Status</span>
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Trash2 size={16} />
              <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Description & Pricing - Top Section */}
      <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 lg:gap-6">
        {property.description && (
          <div className="flex-1 bg-white rounded-lg border border-gray-300 p-3 sm:p-4 md:p-6">
            <h3 className="text-sm sm:text-base lg:text-lg font-bold text-prose-brand mb-2 sm:mb-3">Description</h3>
            <p className="text-prose-black text-xs sm:text-sm whitespace-pre-wrap">{property.description}</p>
          </div>
        )}
        <div className="w-full lg:w-64 bg-white rounded-lg border border-gray-300 p-3 sm:p-4 md:p-6">
          <div className="flex items-center space-x-2 mb-2 sm:mb-3">
            <PoundSterlingIcon className="text-prose-gold-dark flex-shrink-0" size={18} />
            <h3 className="text-sm sm:text-base lg:text-lg font-bold text-prose-brand">Pricing</h3>
          </div>
          <p className="text-prose-black/60 text-xs sm:text-sm mb-1">Price Per Night</p>
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-prose-gold-dark">
            £{property.price}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {/* Gallery */}
        <div className="lg:col-span-2 space-y-3 sm:space-y-4">
          {property.images && property.images.length > 0 && (
            <div>
              <h3 className="text-xs sm:text-sm font-semibold text-prose-brand mb-2 sm:mb-3">Images</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                {property.images.map((image, index) => (
                  <div key={index} className="bg-gray-100 rounded-lg border border-gray-300 overflow-hidden aspect-square">
                    <img
                      src={image.url}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src =
                          'https://via.placeholder.com/200x200?text=Gallery+Image';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Property Details */}
          <div className="bg-white rounded-lg border border-gray-300 p-3 sm:p-4 md:p-6">
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-prose-brand mb-3 sm:mb-4">Property Details</h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
              <div>
                <p className="text-prose-black/60 text-xs font-medium mb-1">Property Type</p>
                <p className="text-sm sm:text-base font-semibold text-prose-brand truncate">{property.type}</p>
              </div>
              <div>
                <p className="text-prose-black/60 text-xs font-medium mb-1">Status</p>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(property.status)}`}>
                  {property.status}
                </span>
              </div>
              <div>
                <p className="text-prose-black/60 text-xs font-medium mb-1">Size</p>
                <p className="text-sm sm:text-base font-semibold text-prose-brand">
                  {property.size || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-prose-black/60 text-xs font-medium mb-1">Bedrooms</p>
                <p className="text-sm sm:text-base font-semibold text-prose-brand">{property.bedrooms}</p>
              </div>
              <div>
                <p className="text-prose-black/60 text-xs font-medium mb-1">Bathrooms</p>
                <p className="text-sm sm:text-base font-semibold text-prose-brand">{property.bathrooms}</p>
              </div>
            </div>

            <div className="border-t border-gray-300 pt-3 sm:pt-4 md:pt-6 pb-3 sm:pb-4 md:pb-6 mb-3 sm:mb-6">
              <p className="text-prose-black/60 text-xs sm:text-sm font-medium mb-1">Address</p>
              <p className="text-prose-black text-xs sm:text-sm break-words">{property.address || 'Not provided'}</p>
            </div>

            {/* Features & Amenities */}
            {(property.features?.length > 0 || property.amenities?.length > 0) && (
              <div className="border-t border-gray-300 pt-3 sm:pt-4 md:pt-6">
                {property.features?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-prose-black/60 text-xs sm:text-sm font-semibold mb-2">Features</p>
                    <div className="flex flex-wrap gap-1 sm:gap-2">
                      {property.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 rounded-lg text-xs sm:text-sm bg-prose-gold/20 text-prose-brand font-medium"
                        >
                          {feature.feature || feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Additional Notes */}
          {property.additional_notes && (
            <div className="bg-white rounded-lg border border-gray-300 p-3 sm:p-4 md:p-6">
              <h3 className="text-base sm:text-lg font-bold text-prose-brand mb-2 sm:mb-3">Additional Notes</h3>
              <div className="h-auto">
                <p className="text-prose-black text-xs sm:text-sm whitespace-pre-wrap">{property.additional_notes}</p>
              </div>
            </div>
          )}

          {/* Info Card - Full Width */}
          <div className="bg-white rounded-lg border border-gray-300 p-3 sm:p-4 md:p-6">
            <h3 className="text-xs sm:text-sm font-semibold text-prose-black/60 mb-3 sm:mb-4">Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
              <div>
                <p className="text-prose-black/60 text-xs font-medium">Created</p>
                <p className="text-prose-black font-medium mt-1">
                  {property.created_at
                    ? new Date(property.created_at).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
              {property.updated_at && (
                <div>
                  <p className="text-prose-black/60 text-xs font-medium">Last Updated</p>
                  <p className="text-prose-black font-medium mt-1">
                    {new Date(property.updated_at).toLocaleDateString()}
                  </p>
                </div>
              )}
              <div>
                <p className="text-prose-black/60 text-xs font-medium">Property ID</p>
                <p className="text-prose-black font-bold font-mono text-sm mt-1">#{property.id}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Booking Calendar */}
        <div className="space-y-3 sm:space-y-4">
          {/* Booking Calendar */}
          <BookingCalendar property={property} bookedDates={bookedDates} />
          
          {/* Manual Blocked Dates List */}
          <ManualBlockedDatesList propertyId={property.id} />
        </div>
      </div>

      {/* Status Update Modal */}
      <Modal
        isOpen={isStatusModalOpen}
        onRequestClose={() => {
          setIsStatusModalOpen(false);
          setSelectedStatus('');
        }}
        className="bg-white rounded-lg shadow-2xl w-full max-w-sm p-4 sm:p-6 outline-none mx-4"
        overlayClassName="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        ariaHideApp={false}
      >
        <h2 className="text-lg sm:text-xl font-bold text-prose-brand mb-4">Update Status</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-prose-black mb-2">New Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-xs sm:text-sm"
            >
              <option value="" disabled>Select a status...</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
            <button
              onClick={handleStatusUpdate}
              disabled={isUpdating || !selectedStatus}
              className="flex-1 px-3 sm:px-4 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 font-semibold text-sm"
            >
              {isUpdating ? 'Updating...' : 'Update'}
            </button>
            <button
              onClick={() => {
                setIsStatusModalOpen(false);
                setSelectedStatus('');
              }}
              disabled={isUpdating}
              className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-prose-brand rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 font-semibold text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        propertyTitle={property.title}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ViewProperties;
